// A right-docked DOM "messenger" panel for the player ↔ avatar conversation.
// Pure DOM (no Phaser): native scrolling + auto-scroll + text wrapping for a real chat thread.
// DialogueScene owns the wiring (server send/receive, keyboard gating, inDialogue) and feeds this
// component via callbacks, so the panel stays framework-agnostic and easy to reason about.

export interface ChatInfos {
    moods: { label: string; value: number; color: string }[]
    amis: string[]
    connus: string[]
    /** evolving self-concept (one first-person sentence) — shown as an italic blurb atop the card */
    selfConcept?: string
    /** "côte de popularité" 0..100 (connectedness + social buzz) */
    aura?: number
    /** props this agent created for the community + how many times others used them */
    creations?: { label: string; uses: number }[]
}

export interface ChatPanelOpts {
    displayName: string
    portraitSrc: string
    header: { role: string; emotion?: string; action?: string }
    infos: ChatInfos
    quicks: string[]
    /** the quick label that CLOSES the panel instead of sending (e.g. "À plus") */
    closeLabel: string
    onSend: (text: string) => void
    onClose: () => void
    onInputFocus: (focused: boolean) => void
}

const STYLE_ID = 'lacaze-chat-style'
const CSS = `
.lacaze-chat{position:fixed;top:0;right:0;height:100dvh;width:min(420px,92vw);z-index:1000;display:flex;
 flex-direction:column;background:#10151f;border-left:2px solid #90cdf4;font-family:sans-serif;color:#fff;
 box-shadow:-8px 0 24px rgba(0,0,0,.45)}
.lc-header{display:flex;align-items:center;gap:10px;padding:10px 12px;border-bottom:1px solid #1f2937}
.lc-ava{width:44px;height:44px;border-radius:8px;background:#0b0f17;flex:0 0 auto}
.lc-id{flex:1;min-width:0}
.lc-id b{color:#90cdf4;font-size:15px;display:block;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.lc-id .sub{color:#cbd5e0;font-size:12px}
.lc-id .act{color:#a0aec0;font-size:11px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.lc-actions button{background:none;border:none;color:#90cdf4;font-size:17px;cursor:pointer;padding:4px 6px;line-height:1}
.lc-infos{padding:8px 12px;border-bottom:1px solid #1f2937;display:none;font-size:12px}
.lc-infos.open{display:block}
.lc-moodrow{display:flex;align-items:center;gap:8px;margin:3px 0}
.lc-moodrow .ml{width:56px;color:#cbd5e0}
.lc-bar{flex:1;height:8px;background:#2d3748;border-radius:4px;overflow:hidden}
.lc-bar i{display:block;height:100%}
.lc-moodrow .mv{width:30px;text-align:right;color:#a0aec0}
.lc-rel{margin-top:6px;color:#cbd5e0;line-height:1.4}
.lc-rel .amis{color:#f6ad55}
.lc-thread{flex:1;overflow-y:auto;padding:12px;display:flex;flex-direction:column;gap:8px}
.lc-msg{max-width:82%;padding:7px 10px;border-radius:12px;font-size:14px;line-height:1.35;white-space:pre-wrap;word-break:break-word}
.lc-msg.them{align-self:flex-start;background:#1f2937;border-bottom-left-radius:4px}
.lc-msg.me{align-self:flex-end;background:#1d4ed8;border-bottom-right-radius:4px}
.lc-typing{align-self:flex-start;color:#718096;font-size:12px;font-style:italic}
.lc-quick{display:flex;flex-wrap:wrap;gap:6px;padding:8px 12px;border-top:1px solid #1f2937}
.lc-quick button{background:#1d4ed8;color:#fff;border:1px solid #90cdf4;border-radius:14px;padding:5px 10px;font-size:12px;cursor:pointer}
.lc-quick button:hover{background:#2563eb}
.lc-say{margin:0 12px 12px;padding:9px 11px;font-size:14px;color:#fff;background:#0b0f17;border:2px solid #90cdf4;border-radius:10px;outline:none}
`

function ensureStyle(): void {
    if (document.getElementById(STYLE_ID)) return
    const s = document.createElement('style')
    s.id = STYLE_ID
    s.textContent = CSS
    document.head.appendChild(s)
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] {
    const e = document.createElement(tag)
    if (cls) e.className = cls
    if (text != null) e.textContent = text // textContent → safe against HTML/script injection
    return e
}

export class ChatPanel {
    private root: HTMLDivElement
    private thread: HTMLDivElement
    private infosEl: HTMLDivElement
    private input: HTMLInputElement
    private actEl: HTMLDivElement
    private typingEl?: HTMLDivElement
    private readonly name: string

    constructor(private opts: ChatPanelOpts) {
        ensureStyle()
        this.name = opts.displayName
        this.root = el('div', 'lacaze-chat')

        // header
        const header = el('div', 'lc-header')
        const img = el('img', 'lc-ava'); img.src = opts.portraitSrc; img.alt = opts.displayName
        const idBox = el('div', 'lc-id')
        idBox.appendChild(el('b', undefined, opts.displayName))
        const sub = el('div', 'sub', opts.header.role + (opts.header.emotion ? ` · ${opts.header.emotion}` : ''))
        idBox.appendChild(sub)
        this.actEl = el('div', 'act', opts.header.action ? `Là : ${opts.header.action}` : '')
        idBox.appendChild(this.actEl)
        const actions = el('div', 'lc-actions')
        const infoBtn = el('button', undefined, 'ℹ'); infoBtn.title = 'Infos'
        const closeBtn = el('button', undefined, '✕'); closeBtn.title = 'Fermer'
        infoBtn.onclick = () => this.infosEl.classList.toggle('open')
        closeBtn.onclick = () => opts.onClose()
        actions.append(infoBtn, closeBtn)
        header.append(img, idBox, actions)

        // infos (collapsed)
        this.infosEl = el('div', 'lc-infos')
        this.renderInfos(opts.infos)

        // thread
        this.thread = el('div', 'lc-thread')

        // quick replies
        const quick = el('div', 'lc-quick')
        for (const label of opts.quicks) {
            const b = el('button', undefined, label)
            b.onclick = () => { if (label === opts.closeLabel) opts.onClose(); else opts.onSend(label) }
            quick.appendChild(b)
        }

        // input
        this.input = el('input', 'lc-say') as HTMLInputElement
        this.input.type = 'text'
        this.input.maxLength = 200
        this.input.placeholder = `Écris à ${opts.displayName}…  (Entrée)`
        this.input.addEventListener('focus', () => opts.onInputFocus(true))
        this.input.addEventListener('blur', () => opts.onInputFocus(false))
        this.input.addEventListener('keydown', (e) => {
            e.stopPropagation()
            if (e.key === 'Enter') {
                const v = this.input.value.trim()
                if (v) { this.input.value = ''; opts.onSend(v) }
            } else if (e.key === 'Escape') {
                opts.onClose()
            }
        })

        this.root.append(header, this.infosEl, this.thread, quick, this.input)
        document.body.appendChild(this.root)
        setTimeout(() => { try { this.input.focus() } catch { /* */ } }, 50)
    }

    /** Player's own message bubble (right). Shows a typing indicator unless replaying history. */
    appendMe(text: string, quiet = false): void {
        this.thread.appendChild(el('div', 'lc-msg me', text))
        if (!quiet) this.showTyping()
        this.scrollDown()
    }

    /** Agent's reply bubble (left). Clears the typing indicator. */
    appendThem(text: string, _quiet = false): void {
        this.hideTyping()
        this.thread.appendChild(el('div', 'lc-msg them', text))
        this.scrollDown()
    }

    /** Clear the thread and replay a full message list (server-loaded history). */
    setThread(messages: { who: 'me' | 'them'; text: string }[]): void {
        this.thread.replaceChildren()
        this.typingEl = undefined
        for (const m of messages) m.who === 'me' ? this.appendMe(m.text, true) : this.appendThem(m.text, true)
    }

    setHeaderAction(action?: string): void {
        this.actEl.textContent = action ? `Là : ${action}` : ''
    }

    setInfos(infos: ChatInfos): void { this.renderInfos(infos) }

    destroy(): void { this.root.remove() }

    private showTyping(): void {
        if (this.typingEl) return
        this.typingEl = el('div', 'lc-typing', `${this.name} écrit…`)
        this.thread.appendChild(this.typingEl)
    }

    private hideTyping(): void {
        this.typingEl?.remove()
        this.typingEl = undefined
    }

    private scrollDown(): void { this.thread.scrollTop = this.thread.scrollHeight }

    private renderInfos(infos: ChatInfos): void {
        this.infosEl.replaceChildren()
        if (infos.selfConcept) {
            const sc = infos.selfConcept.length > 180 ? infos.selfConcept.slice(0, 179).replace(/\s+\S*$/, '') + '…' : infos.selfConcept
            const self = el('div', 'lc-self', `« ${sc} »`)
            self.style.cssText = 'font-style:italic;opacity:.85;margin:0 0 8px;font-size:12px;line-height:1.35'
            this.infosEl.appendChild(self)
        }
        for (const m of infos.moods) {
            const row = el('div', 'lc-moodrow')
            row.appendChild(el('span', 'ml', m.label))
            const bar = el('div', 'lc-bar')
            const fill = el('i'); fill.style.width = `${Math.max(0, Math.min(100, m.value))}%`; fill.style.background = m.color
            bar.appendChild(fill)
            row.appendChild(bar)
            row.appendChild(el('span', 'mv', String(m.value)))
            this.infosEl.appendChild(row)
        }
        if (typeof infos.aura === 'number') {
            const a = el('div', 'lc-aura', `Côte de popularité · ${infos.aura}/100`)
            a.style.cssText = 'margin:6px 0 2px;font-size:12px;opacity:.9'
            this.infosEl.appendChild(a)
        }
        if (infos.creations && infos.creations.length) {
            const c = el('div', 'lc-creations', 'Créations : ' + infos.creations.map(x => `${x.label} (utilisé ${x.uses}×)`).join(', '))
            c.style.cssText = 'margin:2px 0 2px;font-size:12px;opacity:.9;color:#b794f4'
            this.infosEl.appendChild(c)
        }
        if (infos.amis.length || infos.connus.length) {
            const rel = el('div', 'lc-rel')
            if (infos.amis.length) {
                rel.appendChild(el('span', 'amis', 'Amis avec '))
                rel.appendChild(document.createTextNode(infos.amis.join(', ')))
                if (infos.connus.length) rel.appendChild(el('br'))
            }
            if (infos.connus.length) {
                rel.appendChild(document.createTextNode('Connaît '))
                rel.appendChild(document.createTextNode(infos.connus.join(', ')))
            }
            this.infosEl.appendChild(rel)
        }
    }
}
